import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
} from 'react';
import { classNames } from '../../../utils';
import { nullFn, uniqueId, isChecked } from '../utils';
import { AmPmSelectProps } from '../types';

type AmPmSelectHandle = {
  focus: () => void;
};

export const AmPmSelect = forwardRef<AmPmSelectHandle, AmPmSelectProps>(
  (
    {
      id = undefined,
      value = undefined,
      defaultValue = undefined,
      onClick = nullFn,
      onFocus = nullFn,
      onChange = nullFn,
      isFocused = false,
      tabIndex = undefined,
    },
    ref,
  ): JSX.Element => {
    const amInputId = uniqueId('am');
    const pmInputId = uniqueId('pm');

    const fieldSetRef = useRef<HTMLInputElement>(null);

    const handleOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target;
      onChange(value);
    };

    const handleFocus = () => {
      if (!value) {
        onChange(defaultValue || 'AM');
      }
      onFocus();
    };

    useImperativeHandle(ref, () => ({
      focus: focus,
    }));

    useEffect(() => {
      if (isFocused) focus();
    }, [isFocused]);

    const handleOnBlur = (event: React.FocusEvent<HTMLInputElement>) => {
      event.preventDefault();
    };

    const isInputChecked = (inputValue: string): boolean => {
      return isChecked({ inputValue, value, defaultValue });
    };

    const focus = () => {
      if (fieldSetRef.current) {
        fieldSetRef.current.focus();
      }
    };

    return isFocused ? (
      <div
        id={id}
        ref={fieldSetRef}
        role='presentation'
        aria-label='am-pm selector'
        defaultValue={value}
        tabIndex={tabIndex}
        onFocus={handleFocus}
        placeholder='--'
        className='m-0 flex h-full w-20 flex-shrink-0 flex-col rounded-sm border-0 border-none bg-transparent p-0 text-center focus:outline-none'
      >
        <input
          type='radio'
          id={amInputId}
          name='amOrPm'
          value='AM'
          checked={isInputChecked('AM')}
          onChange={handleOnChange}
          onClick={onClick}
          onBlur={handleOnBlur}
          className='hidden'
        />
        <label
          htmlFor={amInputId}
          className={classNames(
            'flex w-full cursor-pointer items-center justify-center rounded-t-md text-center text-sm font-semibold uppercase hover:bg-green-light',
            isInputChecked('AM')
              ? 'bg-green text-white hover:text-green'
              : 'bg-green-lightest text-green hover:text-white',
          )}
          style={{ height: '50%' }}
        >
          am
        </label>
        <input
          type='radio'
          id={pmInputId}
          name='amOrPm'
          value='PM'
          checked={isInputChecked('PM')}
          onChange={handleOnChange}
          onClick={onClick}
          onBlur={handleOnBlur}
          className='hidden'
        />
        <label
          htmlFor={pmInputId}
          className={classNames(
            'flex w-full cursor-pointer items-center justify-center rounded-b-md text-center text-sm font-semibold uppercase  hover:bg-green-light ',
            isInputChecked('PM')
              ? 'bg-green text-white hover:text-green'
              : 'bg-green-lightest text-green hover:text-white',
          )}
          style={{ height: '50%' }}
        >
          pm
        </label>
      </div>
    ) : (
      <input
        ref={fieldSetRef}
        aria-label='am-pm selector'
        defaultValue={value}
        tabIndex={tabIndex}
        onFocus={handleFocus}
        placeholder='--'
        className='m-0 h-full w-20 rounded-sm border-none bg-transparent text-center text-2xl uppercase'
      />
    );
  },
);
